import { jss } from 'react-jss'
import { LIGHT_THEME, ThemeToken, darkVarName } from 'components/theme'

/**
 * Dark mode for colours that never go through THEME.
 *
 * Every stylesheet in the app — ours and the shared component library's
 * (@okcredit/web-lib, which hard-codes its palette) — is built by the same JSS
 * instance. This plugin rewrites literal colours as they pass through:
 *
 *     background: '#ffffff'   →   background: var(--okc-d-brandWhite, #ffffff)
 *
 * The `--okc-d-*` variables exist ONLY under `:root[data-theme='dark']`
 * (components/theme.ts), so in light mode the var() falls back to the original
 * literal and nothing changes by even one shade. In dark mode the literal takes
 * the dark value of the nearest token for its ROLE: a white background becomes a
 * dark surface, but white text stays white (it sits on a coloured fill).
 *
 * Inline `style={{}}` props are not JSS and are not covered.
 */

type Role = 'fg' | 'bg' | 'border'

const roleOf = (prop: string): Role | null => {
  const name = prop.toLowerCase().replace(/-/g, '')
  if (name === 'background' || name === 'backgroundcolor') {
    return 'bg'
  }
  if (name === 'color' || name === 'fill' || name === 'stroke' || name === 'caretcolor') {
    return 'fg'
  }
  if (name.indexOf('border') === 0 || name === 'outline' || name === 'outlinecolor') {
    // border-radius / border-width / border-style carry no colour
    return /radius|width|style|collapse|spacing/.test(name) ? null : 'border'
  }
  return null
}

const COLOR_PATTERN = /#[0-9a-fA-F]{6}\b|#[0-9a-fA-F]{3}\b|\bwhite\b|\bblack\b/g
const INK_PATTERN = /rgba\(\s*(0,\s*0,\s*0|33,\s*33,\s*33|34,\s*47,\s*47)\s*,\s*([0-9.]+)\s*\)/g

const toRgb = (literal: string): [number, number, number] | null => {
  const value = literal.toLowerCase()
  if (value === 'white') {
    return [255, 255, 255]
  }
  if (value === 'black') {
    return [0, 0, 0]
  }
  let hex = value.slice(1)
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('')
  }
  const number = parseInt(hex, 16)
  if (Number.isNaN(number)) {
    return null
  }
  // eslint-disable-next-line no-bitwise
  return [(number >> 16) & 255, (number >> 8) & 255, number & 255]
}

// Exact light value → token, so a literal that IS a token keeps that token's role
const EXACT: Record<string, ThemeToken> = {}
;(Object.keys(LIGHT_THEME) as ThemeToken[]).forEach((token) => {
  const value = LIGHT_THEME[token].toLowerCase()
  if (/^#[0-9a-f]{6}$/.test(value) && !EXACT[value]) {
    EXACT[value] = token
  }
})

const pickToken = (role: Role, literal: string): ThemeToken | null => {
  const rgb = toRgb(literal)
  if (!rgb) {
    return null
  }
  const [r, g, b] = rgb
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255
  const spread = Math.max(r, g, b) - Math.min(r, g, b)
  // The Android grey ramp is teal-tinted (#CBDCDC, #AFCACA, #84ACAC, #426262:
  // green == blue, red lower). Those are neutrals, not colours.
  const isTealGrey = spread <= 50 && Math.abs(g - b) <= 6 && r < g
  const saturation = isTealGrey ? 0 : spread
  const hex = `#${rgb.map((part) => part.toString(16).padStart(2, '0')).join('')}`

  if (role === 'fg') {
    // Light text sits on a coloured or dark fill: leave it alone
    if (luminance > 0.7) {
      return null
    }
    if (saturation > 26) {
      return EXACT[hex] && !/White|Light|Tint|Medium/.test(EXACT[hex]) ? EXACT[hex] : null
    }
    if (luminance <= 0.2) {
      return 'brandBlack'
    }
    if (luminance <= 0.33) {
      return 'brandBlackAlt'
    }
    if (luminance <= 0.42) {
      return 'infoGrey'
    }
    return luminance <= 0.55 ? 'brandDarkerGrey' : 'brandMediumGrey'
  }

  if (role === 'bg') {
    if (saturation > 8) {
      // Pale tints keep their hue family; strong colours are left alone
      if (luminance < 0.85 || saturation > 70) {
        return EXACT[hex] || null
      }
      if (g >= r && g >= b) {
        return 'brandLightGreen'
      }
      if (b >= r && b >= g) {
        return 'brandLightBlue'
      }
      return g - b > 14 ? 'brandTintOrange' : 'brandLightRed'
    }
    if (luminance >= 0.985) {
      return 'brandWhite'
    }
    if (luminance >= 0.965) {
      return 'brandLightestGrey'
    }
    if (luminance >= 0.93) {
      return 'lightGrey'
    }
    if (luminance >= 0.74) {
      return 'brandBGMedium'
    }
    // Mid greys are disabled fills (the library's disabled button is #afafaf /
    // #e0e0e0 under white text): a dark neutral keeps that text readable
    if (luminance >= 0.45) {
      return 'brandLightGrey'
    }
    // Dark greys used as fills (tooltips, dark chips) stay dark
    return luminance <= 0.2 ? 'surfaceInverse' : null
  }

  // border
  if (saturation > 26) {
    return EXACT[hex] && !/White|Light|Tint|Medium/.test(EXACT[hex]) ? EXACT[hex] : null
  }
  if (luminance >= 0.985) {
    return 'brandWhite'
  }
  if (luminance >= 0.6) {
    return 'brandLightGrey'
  }
  return luminance <= 0.2 ? 'brandBlack' : 'brandMediumGrey'
}

const cache: Record<string, string> = {}

const convert = (role: Role, value: string): string => {
  const key = `${role}|${value}`
  if (cache[key] !== undefined) {
    return cache[key]
  }
  let next = value
  if (!value.includes('var(') && !value.includes('url(')) {
    // A gradient or multi-colour background is a drawing, not a surface
    const isPlainBackground = role !== 'bg' || !/gradient|,.*#|#.*,/.test(value)
    if (isPlainBackground) {
      next = next.replace(COLOR_PATTERN, (literal) => {
        const token = pickToken(role, literal)
        return token ? `var(${darkVarName(token)}, ${literal})` : literal
      })
      next = next.replace(INK_PATTERN, (literal, triplet, alpha) => {
        // Translucent black: ink for text / hairlines, a wash for faint fills.
        // The original triplet is the fallback, so light mode is untouched.
        if (role !== 'bg' || parseFloat(alpha) <= 0.12) {
          return `rgba(var(${darkVarName('inkRgb')}, ${triplet}), ${alpha})`
        }
        // A dialog backdrop: 30–50% black barely dims a dark page, so dark mode
        // uses the stronger scrim token
        return parseFloat(alpha) < 0.6 ? `var(${darkVarName('scrim')}, ${literal})` : literal
      })
    }
  }
  cache[key] = next
  return next
}

const convertStyle = (style: any): any => {
  if (!style || typeof style !== 'object') {
    return style
  }
  Object.keys(style).forEach((prop) => {
    const value = style[prop]
    if (typeof value !== 'string') {
      return
    }
    const role = roleOf(prop)
    if (role) {
      style[prop] = convert(role, value)
    }
  })
  return style
}

let installed = false

export const installJssDarkMode = () => {
  if (installed) {
    return
  }
  installed = true
  jss.use({
    onProcessStyle: (style: any) => convertStyle(style),
    // Function values (dynamic styles) arrive one property at a time
    onChangeValue: (value: any, prop: string) => {
      const role = typeof value === 'string' ? roleOf(prop) : null
      return role ? convert(role, value) : value
    }
  } as any)
}
